<template>
  <v-container grid-list-lg>
    <v-layout wrap>
      <v-flex xs6 class='pa-3'>
        <v-card>
          <v-card-title class='headline font-weight-regular oem-color'>
            Device Settings
          </v-card-title>
          <v-card-text>
            <device-info :authenticated='true'></device-info>
          </v-card-text>
        </v-card>
        <div class='pt-4'>
          <v-card>
            <v-card-title class='headline font-weight-regular oem-color'>
              General Settings
            </v-card-title>
            <v-card-text>
              <div class='ma-3'>
                <v-layout row wrap>
                  <v-flex xs11>
                    <v-select
                      v-model='storeState.data.general.defaults.backlight'
                      :items='backlightOptions'
                      label='Display backlight'
                      @change='validate()'
                    ></v-select>
                  </v-flex>
                  <v-flex xs11>
                    <v-select
                      v-model='storeState.data.general.defaults.displayScroll'
                      :items='displayScrollOptions'
                      label='Display scrolling'
                      @change='validate()'
                    ></v-select>
                  </v-flex>
                  <v-flex xs11>
                    <v-select
                      v-model='storeState.data.general.defaults.powerFactorSign'
                      :items='powerFactorOptions'
                      label='Power factor sign convention'
                      @change='validate()'
                    ></v-select>
                  </v-flex>
                  <v-flex xs1 class='mt-2'>
                    <v-btn
                      absolute
                      flat
                      class='transparent oem-text-color'
                      icon
                      @click='helpDialog = true'
                    >
                      <v-icon class='pt-2 oem-text-color'>
                        help_outline
                      </v-icon>
                    </v-btn>
                  </v-flex>
                  <v-flex xs11>
                    <v-select
                      v-model='storeState.data.general.defaults.frequency'
                      :items='frequencyOptions'
                      label='Frequency'
                      @change='validate()'
                    ></v-select>
                  </v-flex>
                  <v-flex xs11>
                    <v-text-field
                      v-model.number='storeState.data.general.defaults.demandWindow'
                      mask='###'
                      v-validate="'required|numeric|min_value:1|max_value:120'"
                      data-vv-name='demand window'
                      :error-messages="errors.collect('demand window')"
                      hint='(minutes)'
                      class='pt-1 oem-text-color'
                      label='Demand window'
                      @change='validate()'
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs11>
                    <v-text-field
                      v-model.number='storeState.data.general.defaults.billingCycleDay'
                      mask='###'
                      v-validate="'required|numeric|min_value:1|max_value:28'"
                      data-vv-name='billing cycle day'
                      :error-messages="errors.collect('billing cycle day')"
                      hint='(day of month)'
                      class='pt-1 oem-text-color'
                      label='Billing cycle day'
                      @change='validate()'
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs5 class='mt-2'>
                    <v-text class='oem-quaternary-text-color'>
                      Billing cycle time
                    </v-text>
                  </v-flex>
                  <v-flex xs6 class='mt-2'>
                    <v-text class='ml-2 oem-text-color'>
                      {{ new Date(storeState.data.general.defaults.billingCycleTime === undefined ? 0 : storeState.data.general.defaults.billingCycleTime * 1000).toLocaleTimeString('en-US', {hour:'numeric', minute:'2-digit', timeZone:'UTC'}) }}
                    </v-text>
                  </v-flex>
                  <v-flex xs1>
                    <v-btn
                      absolute
                      flat
                      class='transparent oem-text-color'
                      icon
                    >
                      <v-icon @click='billingCycleTimePicker = true' class='pt-2 oem-text-color'>
                        access_time
                      </v-icon>
                    </v-btn>
                  </v-flex>
                  <v-flex xs5 class='mt-3'>
                    <v-text class='oem-quaternary-text-color'>
                      Peak demand reset
                    </v-text>
                  </v-flex>
                  <v-flex xs6>
                    <v-btn
                      class='ml-0 oem-text-color'
                      @click='peakDemandResetDialog = true'
                    >
                      Reset
                    </v-btn>
                  </v-flex>
                  <v-flex xs1>
                    <v-btn
                      absolute
                      flat
                      class='transparent oem-text-color'
                      icon
                    >
                      <v-icon @click='peakDemandResetHelp = true' class='pt-2 oem-text-color'>
                        help_outline
                      </v-icon>
                    </v-btn>
                  </v-flex>
                  <v-flex xs5 class='mt-3'>
                    <v-text class='oem-quaternary-text-color'>
                      Passcode
                    </v-text>
                  </v-flex>
                  <v-flex xs6>
                    <v-btn
                      v-if='storeState.data.pinEnabled'
                      class='ml-0 oem-text-color'
                      @click='passwordDialog = true'
                    >
                      Change
                    </v-btn>
                  </v-flex>
                </v-layout>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-flex>
      <v-flex xs6 class='pa-3'>
        <v-card>
          <v-card-title class='headline font-weight-regular oem-color'>
            Site Information
          </v-card-title>
          <v-card-text>
            <div class='ma-3'>
              <v-text-field
                v-model='storeState.data.general.siteInfo.name'
                counter='80'
                :rules='rules80'
                label='Device name'
                class='pt-4'
                @change='validate()'
              ></v-text-field>
              <v-text-field
                v-model='storeState.data.general.siteInfo.site'
                counter='80'
                :rules='rules80'
                label='Installation site'
                class='pt-4'
                @change='validate()'
              ></v-text-field>
              <v-text-field
                v-model='storeState.data.general.siteInfo.addr1'
                counter='80'
                :rules='rules80'
                label='Address 1'
                class='pt-4'
                @change='validate()'
              ></v-text-field>
              <v-text-field
                v-model='storeState.data.general.siteInfo.addr2'
                counter='80'
                :rules='rules80'
                label='Address 2'
                class='pt-4'
                @change='validate()'
              ></v-text-field>
              <v-text-field
                v-model='storeState.data.general.siteInfo.loc1'
                counter='80'
                :rules='rules80'
                label='Location 1'
                class='pt-4'
                @change='validate()'
              ></v-text-field>
              <v-text-field
                v-model='storeState.data.general.siteInfo.loc2'
                counter='80'
                :rules='rules80'
                label='Location 2'
                class='pt-4'
                @change='validate()'
              ></v-text-field>
              <v-text-field
                v-model='storeState.data.general.siteInfo.panel'
                counter='30'
                :rules='rules30'
                label='Panel #'
                class='pt-4'
                @change='validate()'
              ></v-text-field>
              <v-text-field
                v-model='storeState.data.general.siteInfo.type'
                counter='20'
                :rules='rules20'
                label='Panel type'
                class='pt-4'
                @change='validate()'
              ></v-text-field>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog v-model='passwordDialog' persistent max-width='500px'>
        <v-card>
          <v-card-title class='headline font-weight-regular oem-color'>
            Change Passcode
          </v-card-title>
          <v-card-text>
            <div class='ma-3'>
              <v-text-field
                mask='####'
                :append-icon="show1 ? 'visibility' : 'visibility_off'"
                :type="show1 ? 'text' : 'password'"
                v-model='password1'
                name="input-10-2"
                label="New passcode"
                @click:append="show1 = !show1"
                @input='validatePassword'
                :error-messages='errorMsg'
              ></v-text-field>
              <v-text-field
                mask='####'
                v-model='password2'
                :append-icon="show2 ? 'visibility' : 'visibility_off'"
                :type="show2 ? 'text' : 'password'"
                label="Confirm passcode"
                @click:append="show2 = !show2"
                @input='validatePassword'
                :error-messages='errorMsg'
                class='pt-4'
              ></v-text-field>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class='oem-button' flat @click='close'>Cancel</v-btn>
            <v-btn
              class='oem-button'
              flat
              @click='save'
              :disabled='!valid'
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
    </v-dialog>
    <v-dialog v-model='billingCycleTimePicker' full-width width="290px">
      <v-time-picker
        v-model='billingCycleTime'
        scrollable
      >
      </v-time-picker>
    </v-dialog>
    <v-dialog v-model='peakDemandResetDialog' max-width='500px'>
      <v-card>
        <v-card-title class='headline font-weight-regular oem-color'>
          Peak Demand Reset
        </v-card-title>
        <v-card-text>
          Are you sure you want to reset the peak demand?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class='oem-button' flat @click='peakDemandResetDialog = false'>Cancel</v-btn>
          <v-btn class='oem-button' flat @click='peakDemandResetDialog = false, peakDemandReset();'>
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model='peakDemandResetHelp' max-width='500px'>
      <v-card>
        <v-card-title class='headline font-weight-regular oem-color'>
          Peak Demand Reset Help
        </v-card-title>
        <v-card-text>
          The peak demand is reset automatically every month on the day and time as configured in the billing cycle. Pressing the 'Reset' button resets the peak demand directly once.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class='oem-button' flat @click='peakDemandResetHelp = false'>
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model='helpDialog' max-width='800px'>
      <v-card>
        <v-card-title class='headline font-weight-regular oem-color'>
          Legend
        </v-card-title>
        <v-card-text>
          <div>
            <v-img
              :src='images.poweFactorSignHelp1'
              aspect-ratio='2'
              contain
            >
            </v-img>
          </div>
          <div>
            <v-img
              :src='images.poweFactorSignHelp2'
              aspect-ratio='2'
              contain
            >
            </v-img>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class='oem-button' flat @click='helpDialog = false'>
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar"
      :timeout="5000"
      :top='true'
    >
      {{ snackbarText }}
      <v-btn
        class='oem-color-text'
        flat
        @click="snackbar = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import * as auth from '../auth';
import ajax from '../ajax';
// import router from '../routes';
import deviceInfo from './BaseDeviceInfo.vue';

const deviceImage = require('../assets/img/12CH_CT.png');
const pFSHelpImage1 = require('../assets/img/PFSCHelp1.png');
const pFSHelpImage2 = require('../assets/img/PFSCHelp2.png');

export default {
  name: 'General',
  components: {
    deviceInfo,
  },
  computed: {
    binding() {
      const binding = {};
      if (this.$vuetify.breakpoint.mdAndDown) binding.column = true;
      return binding;
    },
    unitDefaultLabels() {
      // const state = this.storeState.password ? 'Enabled' : 'Disabled';
      const labels = [
        'Display backlight', 'Display scrolling', 'Passcode', 'Power factor sign convention',
        'Frequency', 'Demand window', 'Billing cycle day', 'Billing cycle time', 'Peak demand reset',
      ];
      return labels;
    },
    billingCycleTime: {
      // eslint-disable-next-line
      get: function() {
        return new Date(this.storeState.data.general.defaults.billingCycleTime === undefined ? 80280 * 1000 : this.storeState.data.general.defaults.billingCycleTime * 1000).toLocaleTimeString('en-US', {
          hour12: false, hour: 'numeric', minute: '2-digit', timeZone: 'UTC',
        });
      },
      // eslint-disable-next-line
      set: function(newBillingCycleTime) {
        const dateTime = new Date(`1970-01-01T${newBillingCycleTime}:00Z`);
        this.storeState.data.general.defaults.billingCycleTime = Math.round(dateTime.getTime() / 1000);
        // console.log(this.storeState.data.general.defaults.billingCycleTime);
        this.validate();
      },
    },
  },
  data() {
    return {
      general: [
        { key: 'Product', value: 'Power meter' },
        { key: 'Model', value: 'SPM48EDN' },
        { key: 'Serial number', value: '5678912' },
      ],
      storeState: this.$store.state,
      ro: true,
      rules80: [v => v === undefined || v.length <= 80 || 'Max 80 characters'],
      rules20: [v => v === undefined || v.length <= 20 || 'Max 20 characters'],
      rules30: [v => v === undefined || v.length <= 30 || 'Max 30 characters'],
      images: {
        device: deviceImage,
        poweFactorSignHelp1: pFSHelpImage1,
        poweFactorSignHelp2: pFSHelpImage2,
      },
      now: new Date(),
      backlightOptions: [{ text: 'Always on', value: 0 }, { text: 'On button/error', value: 1 }, { text: 'Always off', value: 2 }],
      displayScrollOptions: [{ text: 'Scroll', value: 0 }, { text: 'Static', value: 1 }],
      powerFactorOptions: [{ text: 'IEC', value: 0 }, { text: 'IEEE', value: 1 }],
      frequencyOptions: [{ text: '50 Hz', value: 50 }, { text: '60 Hz', value: 60 }],
      passwordDialog: false,
      valid: true,
      show1: false,
      show2: false,
      password1: '',
      password2: '',
      errorMsg: [],
      billingCycleTimePicker: false,
      peakDemandResetHelp: false,
      peakDemandResetDialog: false,
      snackbar: false,
      snackbarText: '',
      helpDialog: false,
    };
  },
  methods: {
    compareInts(a, b) {
      if (Number(a) === Number(b)) {
        return true;
      }
      return false;
    },
    validatePassword() {
      this.errorMsg = [];
      this.valid = true;
    },
    close() {
      this.passwordDialog = false;
      this.password1 = '';
      this.password2 = '';
    },
    validate() {
      this.$validator.validateAll()
        .then((isValid) => {
          this.storeState.generalValid = isValid;
        });
      this.storeState.generalDirty = true;
    },
    save() {
      if (this.password1.length !== 4 || this.password2.length !== 4) {
        this.errorMsg.push('Passcodes needs to be exactly 4 digits');
        this.valid = false;
      } else if (this.password1 !== this.password2) {
        this.errorMsg.push('Passcodes do not match');
        this.valid = false;
      } else {
        ajax.put('login', { pincode: this.password1, enabled: true })
          .then(() => {
            this.$store.addEventLog('Updated passcode');
            auth.setAccessToken('');
            this.$router.push({ name: 'login' });
          })
          .catch((e) => {
            console.log(e); // eslint-disable-line no-console
            this.$store.addEventLog(`Failed to update passcode. Error ${e}`);
          });
        this.storeState.data.pinEnabled = true;
        this.close();
      }
    },
    disablePin() {
      ajax.put('login', { pincode: '0000', enabled: false })
        .then(() => {
          this.storeState.data.pinEnabled = false;
          const msg = 'Disabled passcode';
          this.$store.addEventLog(msg);
          this.snackbar = true;
          this.snackbarText = msg;
        })
        .catch((e) => {
          console.log(e); // eslint-disable-line no-console
          const msg = `Failed to disable passcode. Error ${e}`;
          this.$store.addEventLog(msg);
          this.snackbar = true;
          this.snackbarText = msg;
        });
    },
    peakDemandReset() {
      ajax.put('system/peakDemandReset')
        .then(() => {
          const msg = 'Peak demand has been manually reset';
          this.$store.addEventLog(msg);
          this.snackbar = true;
          this.snackbarText = msg;
        })
        .catch((e) => {
          console.log(e); // eslint-disable-line no-console
          const msg = `Failed to manually reset the peak demand. Error ${e}`;
          this.$store.addEventLog(msg);
          this.snackbar = true;
          this.snackbarText = msg;
        });
    },
  },
};
</script>
